import { Dispatch, SetStateAction, useCallback, useEffect, useState, useRef } from 'react';
import localforage from 'localforage';
import { UseStateCallback } from '../services/TypeUtils';

/**
 * This implementation uses a lock to delay the initial setStates until after
 * the first load from localstorage has happened
 */
export function useSafePersistedState<T>(
  key: string,
  defaultValue: T,
  useLocalStorage: boolean = false,
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const storage = useLocalStorage ? localStorage : localforage;
  const defaultValueSet = useLocalStorage ? getItemLocalStorage(key, defaultValue) : defaultValue;
  const [state, internalSetState] = useState<T>(defaultValueSet);
  // The key `state` currently holds a loaded value for. Persisting is gated on
  // it, so the default is never written over stored data before the read for
  // that key has landed - on mount, and again whenever `key` changes.
  const [loadedKey, setLoadedKey] = useState<string | undefined>(useLocalStorage ? key : undefined);
  const freshnessLock = useRef<Promise<void> | undefined>(undefined);
  const defaultValueRef = useRef<T>(defaultValue);
  const stateRef = useRef<T>(state);

  defaultValueRef.current = defaultValue;
  stateRef.current = state;

  const setState = useCallback((valueOrUpdate: SetStateAction<T>) => {
    if (!freshnessLock.current) {
      internalSetState(valueOrUpdate);
      return;
    }

    // A caller issuing a plain value before the read has landed can only have
    // derived it from the pre-load value, so remember that base and replay the
    // update on top of what storage returns instead of replacing it.
    const staleBase = stateRef.current;

    freshnessLock.current.finally(() => {
      internalSetState((previous: T) =>
        typeof valueOrUpdate === 'function'
          ? (valueOrUpdate as (previous: T) => T)(previous)
          : replayStaleUpdate(previous, staleBase, valueOrUpdate as T),
      );
    });
  }, []);

  // onMount, and on key change, load from storage
  useEffect(() => {
    if (useLocalStorage) {
      // The first render already seeded state from localStorage; only a later
      // key change has to re-read it.
      if (loadedKey !== key) {
        internalSetState(getItemLocalStorage(key, defaultValueRef.current));
        setLoadedKey(key);
      }
      return undefined;
    }

    if (!storage) return undefined;

    let cancelled = false;
    const { promise, resolve } = Promise.withResolvers<void>();
    freshnessLock.current = promise;

    const fetchPromise = storage.getItem(key) as Promise<string | null>;

    fetchPromise
      .then((value: string | null) => {
        if (cancelled) return;

        let toSave = undefined;

        if (value === undefined || value === null) toSave = defaultValueRef.current;
        else if (value === 'true') toSave = true;
        else if (value === 'false') toSave = false;
        else if (shouldSerialize(value)) toSave = JSON.parse(value) || defaultValueRef.current;
        else toSave = value;

        internalSetState(toSave);
        setLoadedKey(key);
      })
      .catch((err: any) => {
        if (cancelled) return;
        clearSession(err);
        // The read is over, even though it failed: open the write gate so the
        // hook keeps persisting for the rest of its life instead of going mute.
        setLoadedKey(key);
      })
      .finally(() => {
        if (cancelled) return;
        // Releasing the lock keeps later updates synchronous; deferring every
        // setState for the component's lifetime breaks controlled inputs.
        freshnessLock.current = undefined;
        resolve();
      });

    return () => {
      cancelled = true;
      freshnessLock.current = undefined;
    };
  }, [key, useLocalStorage]);

  // saveChanges
  useEffect(() => {
    // Nothing has been read for this key yet - writing now would overwrite the
    // stored value with the default, or with the previous key's state.
    if (loadedKey !== key) return;

    let persistValue: any = state;

    if (persistValue === null || persistValue === undefined) persistValue = defaultValueRef.current;

    if (typeof persistValue === 'object' || Array.isArray(persistValue))
      persistValue = JSON.stringify(persistValue);

    if (useLocalStorage) {
      try {
        storage.setItem(key, persistValue);
      } catch (err: any) {
        clearSession(err);
      }
    } else {
      storage.setItem(key, persistValue)?.catch((err: any) => {
        clearSession(err);
      });
    }
  }, [state, useLocalStorage, key, loadedKey]);

  function clearSession(err: any) {
    console.log(key, err);
    //storage.clear();
  }

  const deleteState = useCallback(() => {
    if (useLocalStorage) {
      try {
        storage.removeItem(key);
      } catch (err: any) {
        clearSession(err);
      }
    } else {
      storage.removeItem(key)?.catch((err: any) => {
        clearSession(err);
      });
    }
  }, [key, useLocalStorage]);

  return [state, setState, deleteState];
}

/**
 * @warning - UNSAFE
 * @deprecated -  use `useSafePersistedState` instead
 */
function usePersistedState<T>(
  key: string,
  defaultValue: T,
  useLocalStorage: boolean = false,
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const storage = useLocalStorage ? localStorage : localforage;

  const defaultValueSet = useLocalStorage ? getItemLocalStorage(key, defaultValue) : defaultValue;

  const [state, setState] = useState<T>(defaultValueSet);
  // See `useSafePersistedState`: writes stay gated until the read for this key
  // has landed, otherwise the mount write clobbers the persisted value.
  const [loadedKey, setLoadedKey] = useState<string | undefined>(useLocalStorage ? key : undefined);
  const defaultValueRef = useRef<T>(defaultValue);

  defaultValueRef.current = defaultValue;

  useEffect(() => {
    if (loadedKey !== key) return;

    let persistValue: any = state;

    if (persistValue === null || persistValue === undefined) persistValue = defaultValueRef.current;

    if (typeof persistValue === 'object' || Array.isArray(persistValue))
      persistValue = JSON.stringify(persistValue);

    if (useLocalStorage) {
      try {
        storage.setItem(key, persistValue);
      } catch (err: any) {
        clearSession(err);
      }
    } else {
      storage.setItem(key, persistValue)?.catch((err: any) => {
        clearSession(err);
      });
    }
  }, [state, useLocalStorage, key, loadedKey]);

  function clearSession(err: any) {
    console.log(key, err);
    //storage.clear();
  }

  useEffect(() => {
    if (useLocalStorage) {
      if (loadedKey !== key) {
        setState(getItemLocalStorage(key, defaultValueRef.current));
        setLoadedKey(key);
      }
      return undefined;
    }

    let cancelled = false;

    (storage?.getItem(key) as Promise<string | null>)
      ?.then((value: string | null) => {
        if (cancelled) return;

        let toSave = undefined;

        if (value === undefined || value === null) toSave = defaultValueRef.current;
        else if (value === 'true') toSave = true;
        else if (value === 'false') toSave = false;
        else if (shouldSerialize(value)) toSave = JSON.parse(value) || defaultValueRef.current;
        else toSave = value;

        setState(toSave);
        setLoadedKey(key);
      })
      .catch((err: any) => {
        if (cancelled) return;
        clearSession(err);
        setLoadedKey(key);
      });

    return () => {
      cancelled = true;
    };
  }, [key, useLocalStorage]);

  const deleteState = useCallback(() => {
    if (useLocalStorage) {
      try {
        storage.removeItem(key);
      } catch (err: any) {
        clearSession(err);
      }
    } else {
      storage.removeItem(key)?.catch((err: any) => {
        clearSession(err);
      });
    }
  }, [key, useLocalStorage]);

  return [state, setState, deleteState];
}

/**
 * Replays a pre-load update on top of the value that was just read from
 * storage. The caller computed `next` from `staleBase` (the default it could
 * see while the read was in flight), so only the fields it actually touched may
 * be applied - everything else must keep the persisted value, or a single
 * keystroke silently wipes the rest of the saved draft.
 * Fields the caller dropped are intentionally left alone: this path only ever
 * has to protect data, never remove it.
 */
function replayStaleUpdate<T>(loaded: T, staleBase: T, next: T): T {
  if (loaded === staleBase) return next;

  if (!isPlainObject(loaded) || !isPlainObject(staleBase) || !isPlainObject(next)) return next;

  const merged: any = { ...loaded };
  const base: any = staleBase;
  const update: any = next;

  Object.keys(update).forEach((field) => {
    if (!(field in base) || base[field] !== update[field]) merged[field] = update[field];
  });

  return merged as T;
}

function isPlainObject(value: any) {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getItemLocalStorage(key: string, defaultValue: any) {
  let value = localStorage.getItem(key);

  if (value === undefined || value === null) return defaultValue;

  if (value === 'true') return true;

  if (value === 'false') return false;

  try {
    if (shouldSerialize(value)) return JSON.parse(value);
  } catch (err) {
    // intentional NOOP
  }

  return value;
}

function shouldSerialize(value: string) {
  return (
    typeof value === 'string' &&
    value !== 'undefined' &&
    (value.startsWith('{') || value.startsWith('['))
  );
}

export default usePersistedState;
